import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';
import { OrderFilters, OrdersExportParams } from '@interfaces';
import { isFilterableUrgency } from '@constants';
import {
  downloadFile,
  getExtensionFromMime,
  isDateRangeOverMaxLookback,
  mergeClientIds,
} from '@utils';
import {
  GoogleAnalyticsService,
  NotificationBannerService,
  SaleOrdersService,
} from '@services';
import { ButtonCustomSizeConfig } from 'nanaykuna-ui-kit/loader';

export interface DownloadOrdersDialogData {
  applied: Partial<OrderFilters>;
  hasFilters: boolean;
  effectiveDateFrom: string;
  effectiveDateTo: string;
}

// Figma 7322:41286 — "Descargar Excel"/"Cancelar" are 56px tall with 40px
// horizontal padding and a 4px icon-text gap; none of the ui-kit's built-in
// `size` presets (sm/md/lg/xl/2xl, capping at 48px) match, hence the custom
// size config rather than a `size` attribute.
const MODAL_BUTTON_SIZE_CONFIG: ButtonCustomSizeConfig = {
  height: 56,
  paddingX: 40,
  textType: 'p16',
  iconSize: 24,
  gap: 4,
};

@Component({
  standalone: false,
  selector: 'digicem-download-orders-dialog',
  templateUrl: './download-orders-dialog.component.html',
})
export class DownloadOrdersDialogComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  protected readonly modalButtonSizeConfig = MODAL_BUTTON_SIZE_CONFIG;

  // Independent from isLoadingExport (D-04) — a slow/failed count call must
  // not spuriously disable the confirm button while the export itself is fine.
  protected isLoadingCount = false;
  // `null` is the error/unresolved/skipped state, rendered as "—" (A-07) —
  // distinct from a resolved `0`, which means "there is nothing to export".
  protected resolvedCount: number | null = null;

  // Independent from isLoadingCount (D-04) — guards re-entry on the confirm
  // click via the early-return in onConfirm(). Deliberately NOT folded into
  // the template's `[enabled]` binding (Figma 7360:43476): `<ui-button>`
  // only renders its loader when `isLoading && enabled` are BOTH true, so
  // `enabled` must stay true while loading or the suffix spinner never
  // shows and the button silently falls back to its disabled render.
  protected isLoadingExport = false;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    protected readonly data: DownloadOrdersDialogData,
    private dialogRef: MatDialogRef<DownloadOrdersDialogComponent>,
    private saleOrdersService: SaleOrdersService,
    private notificationBanner: NotificationBannerService,
    private gaService: GoogleAnalyticsService,
  ) {}

  ngOnInit(): void {
    // Nothing is exportable for a range the confirm button already disables
    // (CA-03/B-21) — skip the call rather than spending a request on a range
    // that can't be downloaded anyway.
    if (this.isOverMaxLookback) {
      return;
    }

    this.isLoadingCount = true;
    this.saleOrdersService
      .getOrdersExportCount(this.exportParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.resolvedCount = response?.count ?? null;
          this.isLoadingCount = false;
        },
        error: err => {
          this.resolvedCount = null;
          this.isLoadingCount = false;
          console.error('Failed to load the orders export count', err);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Shared by the count call (above) and the export call (onConfirm) — one
  // assembly point makes the two requests byte-identical by construction
  // (B-27), instead of two independent builders that could drift apart.
  protected get exportParams(): OrdersExportParams {
    return {
      dateFrom: this.data.effectiveDateFrom,
      dateTo: this.data.effectiveDateTo,
      ferreteriaIds: this.data.applied.ferreteriaIds,
      aliasIds: this.data.applied.aliasIds,
      urgency: this.data.applied.urgency,
      orderNumber: this.data.applied.orderNumber,
    };
  }

  // Always the resolved value from the unconditional count call above —
  // there is no more reuse-the-list's-total branch (CA-02c).
  protected get displayedCount(): number | null {
    return this.resolvedCount;
  }

  protected get totalRecordsLine(): string {
    const count = this.displayedCount;
    if (count === null) {
      return 'En total, descargarás — registros';
    }
    return `En total, descargarás ${count} ${count === 1 ? 'registro' : 'registros'}`;
  }

  // Dev-directed (2026-10-15): the confirm button must stay disabled when
  // there is nothing to export. `null` (still loading/unresolved, or a
  // failed/skipped call) does NOT block — only a resolved 0 does.
  protected get hasNoRecordsToExport(): boolean {
    return this.displayedCount === 0;
  }

  protected get hasDateFilter(): boolean {
    return !!(this.data.applied.dateFrom || this.data.applied.dateTo);
  }

  protected get clientSelectionCount(): number {
    return mergeClientIds(
      this.data.applied.ferreteriaIds,
      this.data.applied.aliasIds,
    ).length;
  }

  protected get hasClientFilter(): boolean {
    return this.clientSelectionCount > 0;
  }

  // Selection count, not a categorical label — mirrors clientSelectionCount
  // exactly (Figma 7322:41301: "Plazo de vencimiento: N filtro(s)", same
  // format as the "Ferretería: N filtro(s)" row).
  protected get urgencySelectionCount(): number {
    return (this.data.applied.urgency ?? []).filter(isFilterableUrgency).length;
  }

  protected get hasUrgencyFilter(): boolean {
    return this.urgencySelectionCount > 0;
  }

  protected get hasOrderNumberFilter(): boolean {
    return !!this.data.applied.orderNumber;
  }

  // Enforced here (modal-level), NOT on OrderFiltersDrawerComponent's own
  // validation — that component is shared with the list's uncapped date
  // filter, and adding the cap there would wrongly constrain the list too.
  protected get isOverMaxLookback(): boolean {
    return isDateRangeOverMaxLookback(
      this.data.effectiveDateFrom,
      this.data.effectiveDateTo,
    );
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected onConfirm(): void {
    if (this.isLoadingExport) {
      return;
    }

    this.isLoadingExport = true;
    const dateFrom = this.data.effectiveDateFrom;
    const dateTo = this.data.effectiveDateTo;

    this.saleOrdersService
      .getOrdersExportFile(this.exportParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ blob, contentType }) => {
          // The deployed Content-Type is still unprobed (B-07) — a 200 whose
          // MIME doesn't map falls through to '.xlsx' rather than shipping an
          // unopenable '.bin' file, and the mismatch is surfaced for diagnosis.
          let extension = getExtensionFromMime(contentType);
          if (extension === 'bin') {
            console.warn(
              `export-excel returned an unmapped Content-Type ("${contentType}") — falling back to .xlsx`,
            );
            extension = 'xlsx';
          }
          downloadFile(blob, `registros_${dateFrom}_${dateTo}.${extension}`);
          this.notificationBanner.showSuccess(
            'Documento descargado',
            'El documento ha sido descargado con éxito.',
          );
          this.gaService.trackEvent('download_orders_excel', {
            event_category: 'Registros',
            event_label: 'Descargar',
            section_title: 'Registros',
          });
        },
        error: () => {
          // Reset here too, not only in `complete` — an Observable that
          // errors never completes, and the dialog must stay retryable
          // (re-entry guard would otherwise wedge the confirm button shut).
          this.isLoadingExport = false;
          this.notificationBanner.showError(
            '¡Ups! Algo salió mal',
            'No pudimos generar el archivo. Espera unos minutos o inténtalo nuevamente.',
          );
        },
        complete: () => {
          this.isLoadingExport = false;
        },
      });
  }
}
